import './home.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';

import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix default marker icons in bundlers (Vite)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const COUNTRY_OPTIONS = [
  // Required order: DE first (default), then AT, CH, then the rest of Europe
  { code: 'DE', label: 'Deutschland' },
  { code: 'AT', label: 'Österreich' },
  { code: 'CH', label: 'Schweiz' },
  { code: 'AL', label: 'Albanien' },
  { code: 'AD', label: 'Andorra' },
  { code: 'AM', label: 'Armenien' },
  { code: 'AZ', label: 'Aserbaidschan' },
  { code: 'BE', label: 'Belgien' },
  { code: 'BA', label: 'Bosnien und Herzegowina' },
  { code: 'BG', label: 'Bulgarien' },
  { code: 'BY', label: 'Belarus' },
  { code: 'DK', label: 'Dänemark' },
  { code: 'EE', label: 'Estland' },
  { code: 'FI', label: 'Finnland' },
  { code: 'FR', label: 'Frankreich' },
  { code: 'GE', label: 'Georgien' },
  { code: 'GR', label: 'Griechenland' },
  { code: 'IE', label: 'Irland' },
  { code: 'IS', label: 'Island' },
  { code: 'IT', label: 'Italien' },
  { code: 'HR', label: 'Kroatien' },
  { code: 'LV', label: 'Lettland' },
  { code: 'LI', label: 'Liechtenstein' },
  { code: 'LT', label: 'Litauen' },
  { code: 'LU', label: 'Luxemburg' },
  { code: 'MT', label: 'Malta' },
  { code: 'MD', label: 'Moldau' },
  { code: 'MC', label: 'Monaco' },
  { code: 'ME', label: 'Montenegro' },
  { code: 'NL', label: 'Niederlande' },
  { code: 'MK', label: 'Nordmazedonien' },
  { code: 'NO', label: 'Norwegen' },
  { code: 'PL', label: 'Polen' },
  { code: 'PT', label: 'Portugal' },
  { code: 'RO', label: 'Rumänien' },
  { code: 'RU', label: 'Russland' },
  { code: 'SM', label: 'San Marino' },
  { code: 'SE', label: 'Schweden' },
  { code: 'RS', label: 'Serbien' },
  { code: 'SK', label: 'Slowakei' },
  { code: 'SI', label: 'Slowenien' },
  { code: 'ES', label: 'Spanien' },
  { code: 'CZ', label: 'Tschechien' },
  { code: 'TR', label: 'Türkei' },
  { code: 'UA', label: 'Ukraine' },
  { code: 'HU', label: 'Ungarn' },
  { code: 'GB', label: 'Vereinigtes Königreich' },
  { code: 'VA', label: 'Vatikanstadt' },
  { code: 'CY', label: 'Zypern' },
];

function FitToMarkers({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    if (!Array.isArray(markers) || markers.length === 0) return;
    // If parent layout uses flex centering, Leaflet may initialize with wrong size.
    // Invalidate first so tiles render in the correct dimensions.
    setTimeout(() => map.invalidateSize(), 0);
    const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [map, markers]);

  return null;
}

export default function CommunityMap() {
  const { user } = useContext(UserContext);
  const [mine, setMine] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ countryCode: 'DE', postalCode: '', isAnonymous: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [meRes, listRes] = await Promise.all([
        api.get('/community-map/me'),
        api.get('/community-map/entries'),
      ]);
      const entry = meRes.data?.entry || null;
      const list = Array.isArray(listRes.data?.items) ? listRes.data.items : [];
      setMine(entry);
      setItems(list);
      if (entry?.postalCode) {
        setForm({
          countryCode: entry.countryCode || 'DE',
          postalCode: entry.postalCode || '',
          isAnonymous: Boolean(entry.isAnonymous),
        });
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Konnte die Community-Karte nicht laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markers = useMemo(() => {
    const groups = new Map();
    const list = Array.isArray(items) ? items : [];

    for (const it of list) {
      const lat = Number(it?.lat);
      const lng = Number(it?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

      const countryCode = it?.countryCode || '';
      const postalCode = it?.postalCode || '';
      const key = `${countryCode}|${postalCode}`;
      const userDisplayName = typeof it?.userDisplayName === 'string' ? it.userDisplayName.trim() : '';

      const existing = groups.get(key);
      if (existing) {
        if (userDisplayName) existing.userDisplayNames.push(userDisplayName);
        if (!existing.placeName && it?.placeName) existing.placeName = it.placeName;
        continue;
      }

      groups.set(key, {
        id: key,
        countryCode,
        postalCode,
        placeName: it?.placeName || null,
        lat,
        lng,
        userDisplayNames: userDisplayName ? [userDisplayName] : [],
      });
    }

    return Array.from(groups.values()).map((g) => {
      const uniqueSorted = Array.from(new Set(g.userDisplayNames)).sort((a, b) => a.localeCompare(b));
      return { ...g, userDisplayNames: uniqueSorted };
    });
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/community-map/me', {
        countryCode: form.countryCode,
        postalCode: String(form.postalCode || '').trim(),
        isAnonymous: Boolean(form.isAnonymous),
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const defaultCenter = [51.1657, 10.4515]; // Deutschland

  return (
    <div className="home-page">
      <section id="top" className={user ? 'hero-offset-if-sidebar' : ''} style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Community‑Karte</h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 820 }}>
          Trage deine Postleitzahl (Wohnort oder nächstgrößere Stadt) ein. Darunter siehst du alle Marker auf der Karte.
        </p>
      </section>

      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        <div className="highlight-card" style={{ padding: 24, marginBottom: 16, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ marginBottom: 6 }}>Mein Marker</h2>
          <p className="highlight-desc" style={{ marginTop: 0 }}>
            Hinweis: Es wird nur eine ungefähre Position über die Postleitzahl gespeichert und angezeigt.
          </p>

          <form onSubmit={submit} style={{ display: 'grid', gap: 10, marginTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Land</div>
                <select value={form.countryCode} onChange={e => setForm(f => ({ ...f, countryCode: e.target.value }))}>
                  {COUNTRY_OPTIONS.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <div style={{ fontSize: 12, color: '#bbb' }}>Postleitzahl</div>
                <input
                  value={form.postalCode}
                  onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                  placeholder="z. B. 10115"
                  required
                />
              </label>
            </div>

            <label style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
              <input
                type="checkbox"
                checked={Boolean(form.isAnonymous)}
                onChange={(e) => setForm((f) => ({ ...f, isAnonymous: e.target.checked }))}
              />
              <span style={{ fontSize: 13, color: '#D4D8DF' }}>
                Anonym anzeigen (statt Display‑Name)
              </span>
            </label>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button disabled={saving || loading} type="submit">
                {saving ? 'Speichern…' : (mine ? 'Aktualisieren' : 'Speichern')}
              </button>
              {error && <span style={{ color: '#ffb4b4', fontSize: 13 }}>{error}</span>}
              {loading && <span style={{ opacity: 0.75, fontSize: 13 }}>Lade…</span>}
            </div>
          </form>
        </div>

        <div className="highlight-card" style={{ padding: 16, alignItems: 'stretch' }}>
          <h2 className="highlight-title" style={{ margin: '8px 8px 12px' }}>Karte</h2>
          <div
            style={{
              height: 560,
              width: '100%',
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <MapContainer center={defaultCenter} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; OpenStreetMap-Mitwirkende'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToMarkers markers={markers} />
              {markers.map(m => (
                <Marker key={m.id} position={[m.lat, m.lng]}>
                  <Popup>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      {(() => {
                        const firstPart = typeof m.placeName === 'string' ? m.placeName.split(',')[0].trim() : '';
                        return firstPart || `PLZ ${m.postalCode} (${m.countryCode})`;
                      })()}
                    </div>
                    <div style={{ fontSize: 13, marginBottom: 6 }}>Mitglieder:</div>
                    {Array.isArray(m.userDisplayNames) && m.userDisplayNames.length > 0 ? (
                      <div style={{ fontSize: 13, lineHeight: 1.35 }}>
                        {m.userDisplayNames.map((n) => (
                          <div key={n}>{n}</div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, opacity: 0.8 }}>—</div>
                    )}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          {markers.length === 0 && (
            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.8 }}>
              Noch keine Marker vorhanden.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
